import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { completeAssignment, listAssignments } from '@/api/assignments';
import type { AssignmentListItem, AssignmentStatus } from '@/types/api';

// Raw query — always fetches the full list. Filtering happens in useAssignments.
function useAllAssignments() {
  return useQuery({
    queryKey: ['miniapp', 'assignments'],
    queryFn: listAssignments,
    staleTime: 30 * 1000,
  });
}

export function useAssignments(status?: AssignmentStatus) {
  const query = useAllAssignments();
  const filtered = useMemo(() => {
    const all = query.data ?? [];
    if (!status) return all;
    return all.filter((a) => a.status === status);
  }, [query.data, status]);
  return { ...query, data: filtered };
}

export function useCompleteAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => completeAssignment(id),
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: ['miniapp', 'assignments'] });
      const previous = qc.getQueryData<AssignmentListItem[]>(['miniapp', 'assignments']);
      if (previous) {
        qc.setQueryData<AssignmentListItem[]>(
          ['miniapp', 'assignments'],
          previous.filter((a) => a.id !== id),
        );
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['miniapp', 'assignments'], ctx.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['miniapp', 'assignments'] });
      qc.invalidateQueries({ queryKey: ['miniapp', 'home'] });
    },
  });
}
